import React, { FC, MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import CloseSmallIcon from 'src/monet/icons/CloseSmallIcon';
import MonetAvatar from 'src/images/monet.inline.png';
import classNames from 'classnames';
import { functionNoop } from 'src/utils/function/noop';
import { Nullable } from 'src/types/nullable.type';
import { useBubbleStore } from 'src/onboarding/store/bubble.store';
import { useUserOnboardingStore } from 'src/onboarding/store/user-onboarding.store';

type OnboardingGuideHostProps = {
  show: boolean;
  showCloseButton?: boolean;
  title?: string | JSX.Element;
  titleClassName?: Nullable<string>;
  content: Nullable<string | JSX.Element>;
  remainingCount?: number;
  notificationMarginCalculatorRef?: MutableRefObject<() => void>;
  onClose?: () => void;
};

export const OnboardingGuideHost: FC<OnboardingGuideHostProps> = ({
  show,
  showCloseButton = false,
  title,
  content,
  remainingCount,
  notificationMarginCalculatorRef,
  onClose = functionNoop,
  titleClassName = 'mb-4',
}) => {
  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const { setBottomMargin, resetBottomMargin, resetBubbleStore } = useBubbleStore();
  const { setOpen, open } = useUserOnboardingStore();
  const { anchorRef, anchorStyle, renderSpotlight } = useSpotlight({
    placement: 'left',
    strategy: 'fixed',
    theme: 'journey',
    boundaryPadding: 24,
    useCurvedArrow: true,
  });

  const setBottomMarginForNotifications = useCallback(() => {
    if (containerElementRef.current) {
      const { height } = containerElementRef.current.getBoundingClientRect();
      setBottomMargin(height);
    }
  }, [setBottomMargin]);

  useEffect(() => {
    if (notificationMarginCalculatorRef) {
      notificationMarginCalculatorRef.current = setBottomMarginForNotifications;
    }
  }, []);

  useEffect(() => {
    if (open) {
      setBottomMarginForNotifications();
    } else if (!open) {
      resetBottomMargin({ marginRight: 56 });
    }

    return () => {
      resetBubbleStore();
    };
  }, [open, resetBubbleStore, resetBottomMargin, setBottomMarginForNotifications]);

  const renderContent = () => {
    return (
      <div className='w-[280px] flex flex-col'>
        <div className='flex'>
          {title && <div className={classNames('grow text-bedrock-serif-h2', titleClassName)}>{title}</div>}
          {showCloseButton && (
            <div className='cursor-pointer' onClick={onClose}>
              <CloseSmallIcon />
            </div>
          )}
        </div>
        {content}
      </div>
    );
  };

  return (
    <div className='fixed z-50 right-6 bottom-6'>
      <div
        ref={anchorRef}
        className={classNames(
          'transition-opacity h-10 w-10 rounded-full bg-cover bg-center cursor-pointer bg-bedrock-white hover:opacity-80',
          {
            invisible: !show,
          }
        )}
        style={{
          ...anchorStyle,
          backgroundImage: `url(${MonetAvatar})`,
        }}
        onClick={() => setOpen(!open)}
      >
        {remainingCount && !open ? (
          <div className='absolute -left-4 -top-2 w-6 h-6 rounded-full bg-bedrock-red text-bedrock-white text-bedrock-p-small-strong flex justify-center items-center bedrock-onboarding-avatar-unread-ring'>
            {remainingCount}
          </div>
        ) : null}
      </div>
      {renderSpotlight({
        content: renderContent(),
        show: show && open,
        containerElementRef,
      })}
    </div>
  );
};
